import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Phone } from "lucide-react";

const PHONE_DISPLAY = "+90 543 793 41 01";
const PHONE_INTL = "905437934101";
const WA_MSG = encodeURIComponent(
  "Merhaba, DR AI FUTBOL hakkında bilgi almak istiyorum. Kulübümüz için demo görüşmesi planlayabilir miyiz?"
);

const WhatsAppFAB = () => {
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(true);

  // Stop the initial pulse animation after 8s for less noise
  useEffect(() => {
    const t = setTimeout(() => setPulse(false), 8000);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      {/* Expanded card */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-5 sm:right-7 z-[60] w-[300px] rounded-2xl bg-dr-ink-1 border border-white/10 shadow-2xl overflow-hidden"
            data-testid="wa-fab-card"
          >
            {/* Header */}
            <div className="bg-[#25D366] p-4 flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center shrink-0">
                <MessageCircle size={20} className="text-[#25D366]" strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-hero text-base text-white uppercase">DR AI FUTBOL</div>
                <div className="text-[11px] text-white/85 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                  Genelde 5 dk içinde yanıt veriyoruz
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-white/85 hover:text-white p-1"
                aria-label="Kapat"
                data-testid="wa-fab-close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 space-y-3">
              <div className="bg-dr-ink-2 rounded-xl rounded-tl-none p-3 max-w-[85%] relative">
                <div className="text-xs text-dr-yellow font-semibold mb-1">DR AI FUTBOL Ekibi</div>
                <p className="text-sm text-white/85 leading-relaxed">
                  Merhaba 👋 Kulübünüz için <strong className="text-white">15 dakikalık ücretsiz demo</strong> ayarlayalım.
                  WhatsApp'tan yazın, hemen yanıt verelim.
                </p>
              </div>
              <a
                href={`https://wa.me/${PHONE_INTL}?text=${WA_MSG}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-[#25D366] hover:bg-[#1ebe57] text-white font-hero uppercase text-sm tracking-wide transition"
                data-testid="wa-fab-action-whatsapp"
              >
                <MessageCircle size={18} strokeWidth={2.5} />
                WhatsApp'tan Mesaj Yaz
              </a>
              <a
                href={`tel:+${PHONE_INTL}`}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl border border-white/15 hover:border-dr-yellow text-white hover:text-dr-yellow font-semibold text-sm transition"
                data-testid="wa-fab-action-call"
              >
                <Phone size={16} strokeWidth={2.5} />
                {PHONE_DISPLAY}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* The floating button itself */}
      <motion.button
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.2, duration: 0.4, type: "spring", stiffness: 200 }}
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-5 right-5 sm:right-7 z-[60] w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white flex items-center justify-center shadow-2xl shadow-[#25D366]/40 transition-colors"
        aria-label="WhatsApp ile iletişim"
        data-testid="wa-fab-trigger"
      >
        {/* Pulse rings */}
        {pulse && !open && (
          <>
            <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-40" />
            <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-30" style={{ animationDelay: "0.5s" }} />
          </>
        )}
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span
              key="x"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="relative flex"
            >
              <X size={24} strokeWidth={2.5} />
            </motion.span>
          ) : (
            <motion.span
              key="wa"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="relative flex"
            >
              <MessageCircle size={26} strokeWidth={2.5} />
            </motion.span>
          )}
        </AnimatePresence>

        {/* Badge */}
        {!open && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-dr-yellow text-dr-black rounded-full text-[10px] font-black flex items-center justify-center border-2 border-dr-black">
            1
          </span>
        )}
      </motion.button>
    </>
  );
};

export default WhatsAppFAB;
